import {
  BodyLinkV3,
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import PageLayoutV3 from '@/app/components/PageLayoutV3';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { AccessAndUseSection } from '../_components/AccessAndUseSection';
import { TermsOfDeliverySection } from '../_components/TermsOfDeliverySection';

const Page = (): JSX.Element => {
  const enable2024Terms = useFeatureFlag('enable-2024-terms-of-use');
  const t = useTranslations();

  return (
    <PageLayoutV3
      pageName="Terms and Conditions"
      containerProps={{
        name: 'terms-and-conditions',
        padding: 'both',
        lang: 'jp',
      }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="terms-and-conditions-ja-page">
        <TypographyRhythm className="col-main">
          <TypographyV3 Element="h1" variant="h1">
            ŌURA利用規約
          </TypographyV3>
          <BodyLinkV3 color="inherit" href="/gen2-terms-and-conditions/jp">
            前 利用規約
          </BodyLinkV3>
          <TypographyV3>
            <em>最終更新：2024年4月2日</em>
          </TypographyV3>
          <TypographyV3>
            ようこそ！
            Ōuraに興味を持っていただきありがとうございます。この利用規約は、ouraring.com（以下「本サイト」といいます）、スマートリング、充電器、その他の電子機器（以下「製品」といいます）、およびŌuraモバイルアプリケーション（以下「モバイルアプリ」といいます）（以下、総称して「本サービス」といいます）の利用に適用される条件を記載したものです。本サイトおよびモバイルアプリは、
            <strong>Ōura Health Oy</strong>およびその関連会社、Ouraring
            Inc.などの子会社が所有・運営しています。
          </TypographyV3>
          <TypographyV3>
            本契約において、弊社は自らを「Ōura」または「Oura」または「弊社」または「当社」と称し、お客様を「お客様」または「顧客」と称しています。本契約では、Ōuraおよびお客様を個別に「当事者」と呼び、総称して「全当事者」と呼びます。
          </TypographyV3>
          <TypographyV3>
            本サイトへのアクセスを含め、本サービスにアクセスまたは使用することにより、お客様は、本契約のすべての条件および参照することにより組み込まれるプライバシーポリシー（
            <BodyLinkV3
              color="inherit"
              href="https://ouraring.com/privacy-policy-oura-health/ja"
              target="_blank"
            >
              https://ouraring.com/privacy-policy-oura-health/ja
            </BodyLinkV3>
            で入手可能）に拘束されることを了承し、明示的に同意するものとします。これらの条件に同意しない場合は、本サービスを利用することはできません。
          </TypographyV3>

          <AccessAndUseSection />

          <TypographyV3 Element="h2" variant="h3">
            顧客責任
          </TypographyV3>
          <TypographyV3>
            お客様は、本サービスの直接的または間接的な使用について、そのアクセスまたは使用が本契約で許可されているかどうか、または本契約に違反しているかどうかを理解することを含め、お客様が単独で責任を負うことを了承します。さらに、お客様は、本サービスの使用に関連するすべての適用法を遵守することに単独で責任を負います。さらに、お客様は、本サービスを合法的な目的のみに使用し、本サービスを通じたすべてのビジネスを、有価証券の提供および販売、マネーロンダリング、およびテロ対策に関するすべての適用される連邦および州の法律および規制を含むがこれらに限定されない、すべての適用される法律および規制に従って行うものとします。
          </TypographyV3>
          <TypographyV3>
            互換性のある独自のハードウェア、モバイルアプリ、インターネットアクセス、セキュリティモバイルアプリ、バックアップデバイスまたはサービス、およびその他の要件を保護するなどして、サービスにアクセスして使用できることを保証および維持する責任はユーザーのみにあります。
            Ouraは、追加のモバイルアプリまたはハードウェアを提供する責任を負わないものとします。さらに、お客様は、適切なセキュリティまたはバックアップデバイスまたはサービスを提供できないなど、サービスの使用に関連して被ったデータ損失またはその他の損害または損失について、Ouraが責任を負わないことに同意するものとします。
          </TypographyV3>
          <TypographyV3>
            お客様は、Ouraがお客様の顧客アカウントについて、現在の連絡先や支払い情報を含む正確かつ最新の情報を持っていることを確認する責任があります。さらに、Ouraからの通信について、関連するお客様のメールアカウントを定期的に確認する責任があります。
          </TypographyV3>
          <TypographyV3>
            セキュリティ手順の一部としてユーザー名、パスワード、認証情報ファイル、またはその他の情報（「認証情報」）が提供された場合、それらの情報を機密情報として扱い、他の個人またはエンティティに認証情報を開示してはなりません。お客様はお客様のアカウントと認証情報がお客様個人のものであることを確認し、さらにお客様のユーザー名、パスワード、または他のセキュリティ情報を使用して他の人にサービスまたはサービスの一部へのアクセスを提供しないことに同意します。お客様は、お客様の資格情報への不正アクセスまたは使用、またはその他のセキュリティ違反について、直ちにOuraに通知するものとします。Ouraは、ユーザー名、パスワード、認証情報ファイル、またはその他の識別子を、ユーザーが選択したか、Ouraが提供したかにかかわらず、いつでも無効にする権利を有します。
          </TypographyV3>
          <TypographyV3>
            Ouraは、本サービスに適切なサポートサービスを提供するために商業的に合理的な努力を払うものとします。上記にかかわらず、本契約は、本サービスのサポートサービスの保証されたレベル、可用性、または所要時間の権利をお客様に与えるものではありません。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            支払いと料金
          </TypographyV3>
          <TypographyV3>
            有料サービスには、当社のサイトおよびモバイルアプリ（「サブスクリプション」）を含む、1回限りの購入またはサブスクリプションサービス（「有料サービス」）の自動更新のサービスが含まれます。当社は、理由の如何を問わず、いつでも有料サービスを変更、一時停止、または中止することができます。Ouraは、どのサービスまたはその一部に支払いが必要かを判断する独自の裁量を留保します。
          </TypographyV3>
          <TypographyV3>
            有料サービスには、将来的に生産される事前注文製品（「事前注文」）が含まれる場合があります。事前注文を行う際に、事前注文料金が請求されます。承認された事前注文の実際の発送日は、事前注文料金の支払い日やOuraの製造スケジュールなど、さまざまな要因によって異なります。事前注文の発送日保証はありません。
          </TypographyV3>
          <TypographyV3>
            お客様は、月額サブスクリプション料金、ユーザー料金、提供料金、およびチェックアウトプロセス中に有料サービスの一部として購入することに同意するその他の料金、手数料、または費用を支払うことに同意するものとします（「料金」）。お客様は、本契約の終了またはキャンセルの前に発生したすべての料金および該当するすべての税金を支払うことに同意するものとします。
          </TypographyV3>
          <TypographyV3>
            お客様は、Ouraが有料サービスの指定された支払い方法に請求することを承認します。受け入れ可能な支払い方法を提供することにより、指定された支払い方法を使用する権限があり、適用される税金を含む購入総額を支払い方法に請求することを当社または第三者の支払い処理業者に許可することを表明および保証するものとします。およびその他の料金。支払い方法が確認できない、無効である、またはその他の方法で受け入れられない場合、有料サービスは一時停止またはキャンセルされる場合があります。お客様は、本サービスの利用を続行するために、お客様が提供する支払い方法に関して発生した問題を解決する必要があります。プロモーションのオファーを受け入れるか、有料サービスに変更を加えると、料金、税金、請求額が異なる場合があります。また、適用される税金や為替レートの変更により、請求額が異なる場合があります。お客様は、当社または当社の第三者の支払い処理業者が、対応する金額を支払い方法に請求することを承認します。法律で義務付けられていない限り、払い戻しは行われません。個々の割引は、その他の割引と併用したり、重複して利用したりすることはできません。この支払い義務は、理由の如何を問わず、本契約の終了またはキャンセル後も存続するものとします。
          </TypographyV3>
          <TypographyV3>
            お客様が当社の第三者の支払い処理業者を通じて購入資金を調達することを選択した場合で、お客様の注文に含まれる1つまたは複数の商品の出荷日が延長された場合、金利を含むローンの支払いは、当社がすべての商品を出荷する前に行われることがあります。なお、既に発生している利息を後から返金することはできませんのでご了承ください。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            サブスクリプション
          </TypographyV3>
          <TypographyV3>
            特定の有料サービスはサブスクリプションベースの購入であり、次の条件が適用されます。
          </TypographyV3>
          <TypographyV3>
            サブスクリプション期間は、有料サービスの購入過程で説明されているように、継続的、月次、または年次の期間（「サブスクリプション期間」）として異なる場合があります。サブスクリプションは、サブスクリプションがキャンセルされるか、Ouraによって一時停止または終了されるまで、追加のサブスクリプション条件で自動更新されます。当社が別途指定しない限り、指定された支払い方法は、各サブスクリプション期間の前または開始時に、サブスクリプション料金に加えて、該当する税金およびその他の料金が請求されます。サブスクリプション期間を請求する前に、該当する料金を通知します。更新は、有料サービスに対して有効な価格で行われます。
          </TypographyV3>
          <TypographyV3>
            お客様は、いつでもサブスクリプションをキャンセルすることができます。お客様のキャンセルは、現在のサブスクリプション期間の終了時に有効となります。サブスクリプションおよび自動支払いをキャンセルするには、アカウント画面から「アカウントのキャンセル」ボタンをクリックするか、電子メール（support@ouraring.com）でご連絡ください。キャンセルしても、それまでに支払われた料金の払い戻しを受ける権利はなく、残りのサブスクリプション期間に対する日割り計算による払い戻しも受けられません。お客様がサブスクリプションをキャンセルした場合でも、お客様が通信に記載されているサブスクリプション停止指示に従ってこれらの通信の受信を拒否しない限り、当社はお客様にプロモーションの通信を送信する場合があることに留意してください。
          </TypographyV3>
          <TypographyV3>
            お客様がサブスクリプションをキャンセルすると、当該サブスクリプションの将来の料金のみがキャンセルされます。お客様が支払った現行のサブスクリプション期間の払い戻しは受けられませんが、その現行のサブスクリプション期間の終了時まで、そのサブスクリプションへの完全なアクセスが継続されます。理由の如何を問わず、当社は、一部または全部のユーザーに対して、返金、割引、またはその他の対価（以下「クレジット」といいます）を提供する場合があります。このようなクレジットの金額および形態、ならびにクレジットを提供するかどうかの判断は、当社の単独かつ絶対的な裁量に委ねられます。ある場合にクレジットが提供されても、将来、同様の場合にクレジットを受け取る権利はなく、また、当社が将来クレジットを提供する義務もありません。
          </TypographyV3>
          <TypographyV3>
            米国外にお住まいのお客様が購入を変更された場合、14日以内（「クーリングオフ期間」）であれば、お客様は全額返金を受けることができます。ただし、クーリングオフ期間中にお客様が加入者として本サービスにログインしたり、その他の方法で利用したり、利用を開始したりしていない場合に限ります。
          </TypographyV3>
          <TypographyV3>
            時折、当社は、特定のサブスクリプションについて、支払いを伴わない一定期間の無料トライアルを提供することがあります。無料トライアルの開始前に、当社は、無料トライアルの終了時に請求される該当するサブスクリプションの料金をお客様に通知します。お客様が上記の手順で無料トライアルの終了前にサブスクリプションをキャンセルしない限り、お客様の無料トライアルが終了すると、当社または当社の第三者支払い処理業者は、お客様のサブスクリプションが継続する限り、お客様の指定支払い方法に対して、サブスクリプション料金に加えて、適用される税金およびその他の料金を定期的に請求します。お客様は、請求を回避するために、無料トライアル期間が終了する前に、サブスクリプションをキャンセルする必要があります。サブスクリプションの取り消し方法は上記のとおりです。
          </TypographyV3>
          <TypographyV3>
            お客様の支払い情報は、第三者の支払い処理業者を通じて処理および保存されます。すべての有料アカウント所有者は、チェックアウト時に詳細が説明される料金の支払いのために、少なくとも1つの有効な支払い方法を保持する必要があります。すべての料金は、お客様の選択に応じて、月単位または年単位で計算され、お客様に請求されます。これらの料金は、受領後直ちに支払われ、変更される場合があります。お客様は、料金には定期的な支払い機能があることを確認し、キャンセル前のすべての定期的な料金について責任を負うものとします。料金は、最初の購入日から月単位または年単位で起算して1日前に、お客様が提供する保存された指定の支払い方法から請求または引き落とされるものとします。
          </TypographyV3>
          <TypographyV3>
            お客様が、無料トライアル期間中に製品を購入または受領した後、6ヵ月以上にわたって本サービスへのログインまたはその他の利用がない場合、当社は、お客様のサブスクリプションを解約し、保留中の購入を取り消し、製品の購入価格のみをお客様に返金する権利を有します。お客様は、無料トライアル期間中のサブスクリプション料金については、返金を受けることはできません。
          </TypographyV3>
          <TypographyV3>
            当社は、「有料サービス」の利用料金、または「有料サービス」の機能や本サービスの一部をいつでも調整する権利を有します。お客様は、当社が有料サービスの料金を随時変更できることを了承するものとします。このような変更を行う場合、当社は、変更の発効日の少なくとも30日前に、お客様のアカウントに関連付けられているメールアドレスを介してお客様に通知します。お客様が本サービスを継続してご利用になることで、お客様は料金の変更に同意されたものとみなされます。お客様は、適用されるすべての税金について単独で責任を負うものとし、法律で要求される場合には税金を請求されます。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            データおよび通信
          </TypographyV3>
          <TypographyV3>
            Ouraは、お客様の本サービスの利用に関する情報を収集し、処理することがあります。お客様は、Ouraが当該情報を収集・使用することに同意し、また、本サービスの提供、マーケティング、改善、およびプライバシーポリシーに記載されているその他の理由のために、第三者のサービスプロバイダーと当該情報を共有することに同意します。Ouraが収集したすべての個人情報は、プライバシーポリシーに基づいて取り扱われます。
          </TypographyV3>
          <TypographyV3>
            本契約の条件に同意し、お客様の連絡先情報をOuraに提供することにより、お客様は、Oura、その関連会社、および代理人が、お客様がOuraに提供した郵送先住所、電話番号、またはメールアドレスに随時連絡することに、明示的に同意するものとします。お客様の同意は、本サービスおよびお客様のアカウント、購読、購入、利用可能なアップグレード、請求および支払い処理の問題、テレマーケティング通信に関連する通知を含むがこれらに限定されない、あらゆる目的のために、電話、メール、テキストメッセージ、またはその他の手段でOuraおよびそのサービスプロバイダーから連絡を受けることに同意することを意味します。当該許可された通信には、自動ダイヤル技術の使用や、録音済みメッセージの使用が含まれる場合があります。お客様は、当社がお客様に連絡する際にお客様のサービスプロバイダーから請求される可能性のある料金について責任を負うものとします。さらにお客様は、上記への同意がOuraサービスの利用条件ではないことを確認し、同意を希望しない場合は、当社に連絡して連絡不可リストへの登録を依頼するか、または当該通信に記載されているオプトアウトメカニズムを使用していつでもオプトアウトすることができます。
          </TypographyV3>
          <TypographyV3>
            当社は、お客様が当社に提供した患者の電子健康記録を構成する可能性のある情報、またはお客様またはエンドユーザーが提供した同様の情報について、本契約の別段の定めにかかわらず、または適用される連邦、州、または国際的な法律、規則、または規制に別に定めがある場合、本契約に基づく一切の責任を負いません。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            知的財産権
          </TypographyV3>
          <TypographyV3>
            ŌURA、Ō、およびOURAは、Ōura Health
            Oyの商標です。Ouraコンテンツ、Oura製品、Oura機能およびサービス、ならびに当社の基礎技術は、米国および諸外国の著作権、商標、特許、知的財産権、およびその他の法律によって保護されています。すべての権利は留保されています。お客様は、本サービスに表示される、または本サービスに関連して使用もしくは表示されるマーク（以下「商標」といいます）を使用するライセンスまたは権利を、黙示またはその他の方法により付与されません。また、本サービスには、それぞれの所有者の登録商標である第三者の商標、商号、製品名、ロゴが含まれていたり、参照されていたりします。いかなる場合でも、お客様は、本商標を使用または複製することはできません。本規約のいかなる条項も、Ouraの書面による明示的な許可なく、本サービスに関連して表示される商標を使用するライセンスまたは権利を付与するものと解釈されることはありません。
          </TypographyV3>
          <TypographyV3>
            本サービスおよび本契約に関連して提供されるすべてのコンテンツ（本サイト、本製品、モバイルアプリ、すべてのテキスト、グラフィックス、ユーザーインターフェース、ビジュアルインターフェース、写真、画像/ビデオ、電子アート、音声/オーディオ、データ、通信プログラム、実行可能コード、コンピュータコード、およびデータ（以下、総称して「コンテンツ」といいます）を含むがこれらに限定されない）は、さまざまな形態でフォーマット、整理、収集されます。本コンテンツ、本サービス、およびその基礎となる技術に含まれる、本コンテンツのデザイン、構造、選択、調整、表現、「ルック＆フィール」、配置、レイアウト、ページ、画面、およびデータベースを含む、さまざまな形態でフォーマット、整理、収集されたもの、および本サービスに関連するその他すべての著作権で保護された作品（以下「著作物」）は、Ouraが独占的に所有、管理、またはライセンス付与されており、米国および国際的な著作権法によって保護されています。お客様は、Ouraの書面による事前の明示的な許可なしに、直接的または間接的に著作物をコピー、複製、修正、派生物の作成、配布、または一般公開しないことに同意するものとします。
          </TypographyV3>
          <TypographyV3>
            お客様が、郵便、メール、電話、またはその他の方法で、本サービスの変更（新機能や機能性を含むがこれに限定されない）を提案または推奨する通信または資料（以下「フィードバック」）を当社に提供した場合、当社は、当該フィードバックに関連する両当事者間のその他の義務または制限にかかわらず、当該フィードバックを自由に使用することができます。当社は、いかなる目的のためにも、フィードバックに含まれるアイデア、ノウハウ、コンセプト、技術、またはその他の知的財産権を、いかなる当事者にも帰属または補償することなく、自由に使用することができますが、当社はフィードバックを使用する必要はありません。
          </TypographyV3>
          <TypographyV3>
            Ouraは、他者の知的財産権を尊重し、著作権またはその他の適用される知的財産法の侵害を主張する通知を迅速に処理し、確認することを方針としています。侵害を主張する通知は、Ouraの指定代理人（ip@ouraring.com）宛に送付する必要があり、以下のすべてを含む必要があります。(i)
            著作権所有者または著作権所有者の代理として行動することを承認された者の署名（物理的または電子的）、(ii)
            侵害されたと主張する著作物の説明、(iii)
            侵害していると主張し、削除されるべきまたはアクセスを無効にされるべき素材の説明、およびOuraの管理者が素材を特定するのに十分な情報。(iv)
            住所、電話番号、電子メールアドレスなど、当社がお客様に連絡を取るのに十分な情報
            (v)
            申し立てられた方法での素材の使用が、著作権所有者、その代理人、または法律によって許可されていないことを誠実に信じる旨の声明
            (vi)
            通知の情報が正確であり、偽証罪に基づき、お客様が著作権所有者であること、または侵害されたと主張する著作権所有者の代理として行動することを許可されていることを示す声明。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            保証に関する免責事項
          </TypographyV3>
          <TypographyV3>
            本サービスは、いかなる種類の保証もなく、「現状のまま」かつ「利用可能な状態で」提供されており、当社およびその子会社、関連会社、役員、取締役、従業員、代理人、パートナー、ライセンサーは、本サービスに関して、明示的または黙示的を問わず、すべての保証および条件を放棄し、権原、商品性、特定目的への適合性、満足のいく品質、および権利を侵害していないことに関する黙示的な保証を明示的に放棄します。当社は、お客様による本サービスの享受の妨害、コンテンツの利用可能性、本サービスに含まれる機能がお客様の要件を満たすこと、本サービスにウィルスやその他の有害な要素が含まれていないこと、本サービスの運営が中断されないこと、エラーがないこと、本サービスの欠陥が修正されること、本サービスに含まれる機能が他のモバイルアプリケーションやハードウェア、またはシステム内で機能することを保証しません。当社または当社の認定代理店が提供する口頭または書面による情報または助言は、保証を構成するものではありません。管轄区域によっては、黙示的保証の除外または消費者の適用される法的権利の制限が認められていないため、上記の除外が適用されない場合があります。
          </TypographyV3>
          <TypographyV3>
            上記にかかわらず、当社は、最初に購入されたエンドユーザーに対し、購入日から1年間、本製品に材料および製造上の欠陥がないことを保証します。購入時に有効な地域の法律で1年を超える保証期間が要求されている場合は、本保証はその法律で要求される範囲まで延長されるものとします。保証期間内に、Ouraは、提供された限定保証を満たしていない本製品の部品に関しては、お客様に無償で修理または交換するものとします。お客様は、関連する輸送費を負担するものとします。交換製品は、当社の判断により、新品または再生品となります。本保証には以下には適用されません。(i)
            傷やへこみを含む通常の摩耗、(ii)
            電池などの本製品に含まれる消耗部品、(iii)
            お客様が本製品に添付された説明書またはウェブサイトに掲載されている説明書に従って本製品を使用しなかったことに起因する損害、(iv)
            事故に起因する損害には適用されません。(事故、水害、火災、誤用、乱用に起因する損害、（5）当社の許可を受けていない者が行ったサービスや本製品の改造に起因する損害、（6）本製品を携帯アプリ以外のアプリケーションやソフトウェアで使用した場合。
          </TypographyV3>
          <TypographyV3>
            当社は、独自の判断により、本製品の修理または交換、あるいは全額返金を行う排他的な権利を有します。このような救済措置は、本限定保証の違反に対するお客様の唯一かつ排他的な救済措置となります。保証の修理および交換には、90日または元の1年保証の残余期間のいずれか長い方の新しい保証が適用されます。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            責任制限
          </TypographyV3>
          <TypographyV3>
            いかなる場合においても、当社、その子会社、関連会社、役員、取締役、従業員、代表者、代理人、パートナー、ライセンサーは、お客様による本サービス、本サービスに関連するコンテンツ、または他のサイト、または本サービスを通じて得られたサービスやアイテムの使用、または使用できないことに起因または関連する、いかなる種類の、いかなる法理論に基づく損害に対しても、直接的、間接的、特別、偶発的、結果的、または懲罰的な損害を含め、責任を負いません。これには、直接損害、間接損害、特別損害、偶発的損害、結果的損害、懲罰的損害が含まれます。これには、人身傷害、苦痛、精神的苦痛、収益の損失、利益の損失、事業または予想される貯蓄の損失、事業機会の損失、事業の中断、使用の損失、営業権の損失、データの損失などが含まれますが、これらに限定されるものではなく、また、不法行為（過失を含む）、契約違反、その他が原因であるかどうかにかかわらず、たとえ予測可能であっても、このような損害が発生することはありません。これらの除外または制限は、当社が当該損害の可能性について警告を受けていたか否かにかかわらず適用されます。
          </TypographyV3>
          <TypographyV3>
            上記は、適用される法律の下で除外または制限することができない責任に影響を与えるものではありません。上記のように責任を排除または制限できない範囲において、契約、不法行為、またはその他の責任理論に基づくかどうかにかかわらず、いかなる場合においても、当社は100ドルを超える請求について責任を負いません。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            注意事項
          </TypographyV3>
          <TypographyV3>
            本サービスは医療機器ではなく、お客様は、本サービスが当社による医療上のアドバイスの提供を伴わないことに明示的に同意するものとします。本サービスは、いかなる疾病または病状の診断、治療、治癒、または予防を目的としたものではありません。本サービスは、情報提供のみを目的としており、医師または医療専門家のサービスに代わるものではありません。
          </TypographyV3>
          <TypographyV3>
            本サービスは、当社または第三者によって提供されるすべての情報、テキスト、写真、画像、イラスト、グラフィック、オーディオ、ビデオ、オーディオビデオクリップ、およびその他の資料を含みます。本サービスは、(a)
            お客様の医師またはその他の医療専門家の助言、(b)
            お客様の医師またはその他の医療専門家への訪問、電話、相談、または (c)
            製品の包装もしくはラベルに記載された情報の代わりに利用することを意図するものではなく、利用するべきではありません。
          </TypographyV3>
          <TypographyV3>
            健康に関する質問がある場合は、速やかに医師またはその他の医療従事者に連絡または相談してください。緊急事態が発生した場合には、直ちに医師または警察に連絡してください。本サービスに掲載されている情報を理由に、医療上の助言を無視したり、医療上の助言を求めるのを遅らせたりしてはいけません。また、本サービスや本サービスで提供される情報を、健康問題の診断や治療のために使用してはいけません。本サービスの全部または一部の送受信、またはインターネット、メール、その他の手段による通信は、お客様と当社との間に医師-患者、セラピスト-患者、その他の医療専門家としての関係を構成または創出するものではありません。
          </TypographyV3>
          <TypographyV3>
            本サービスを通じて提供される情報に基づいて睡眠や活動を変更する場合、または病状に関する質問がある場合は、必ず医師に相談してください。当社は、お客様が本サービスを通じて知り得た情報に起因するいかなる健康問題についても責任を負いません。お客様が本サービスに基づいて睡眠や活動に変更を加える場合、お客様は完全に自己責任で行うことに同意するものとします。自分の体の反応に敏感であることは重要です。例えば、睡眠や活動を変更したことにより、予期せぬ痛み、繰り返しの痛み、長期的な痛み、疲労感、不快感などを感じた場合は、そのような変更を続ける前に医師に相談することをお勧めします。病状やまれにある生まれつきの違いに起因してお客様の生理機能や反応が集団の平均値と大きく異なる場合、本サービスの情報は誤解を招く恐れがあります。
          </TypographyV3>
          <TypographyV3>
            本製品を装着しているときは、固定された構造物や重いものに引っかからないようにご注意ください。本製品を装着しているときに、指が赤くなったり、皮膚が荒れたりした場合は、すぐに外してください。本製品を使用しないで2～3日以上症状が続く場合は、専門家にご相談ください。
          </TypographyV3>
          <TypographyV3>
            当社製品は、いかなる場合でも口の中に入れてはいけません。当社製品は、玩具ではなく、子供が使用することを意図したものではありません。窒息の危険性がありますので、本製品をお子様のそばに置かないでください。
          </TypographyV3>
          <TypographyV3>
            本サービスは、第三者が運営する他のウェブサイトへのリンクを提供する場合があります。お客様は、当該リンクはお客様の便宜のために提供されているものであり、リンク先のサイトの提供者、またはリンク先のサイトの品質、信頼性、その他の特徴や機能について、Ouraが保証、提携、関係、またはスポンサーシップを反映したものではないことを確認し、同意するものとします。さらに、お客様は、リンク先のサイトに関連する事項（リンク先のサイトで提供されているコンテンツ、またはお客様がリンク先のサイトに依存していることを含みますが、これに限定されません）について、当社がいかなる方法でも（お客様が被る可能性のある損失または傷害に関するものを含みますが、これに限定されません）責任を負わないことを確認し、同意するものとします。さらに、お客様による第三者のサイトのご利用には、当該サイトのプライバシーポリシー（またはその欠如）を含む当該サイトに適用される条件が適用されることをご了承ください。第三者が本サービスにリンクしている場合、それは必ずしもOuraによる、またはOuraとの保証、提携、関係、またはスポンサーシップを示すものではありません。また、Ouraは、第三者が本サービスにリンクしていることを認識していない場合もあります。
          </TypographyV3>
          <TypographyV3>
            Ouraが所有していないその他のコンテンツは、それぞれの所有者が所有しています。お客様は、当該コンテンツがその所有者によって提供されるものであり、当該コンテンツの提供者に対するOuraの保証、提携、関係、またはスポンサーシップを反映したものではないことを確認し、これに同意するものとします。さらに、お客様は、第三者が提供したコンテンツ（お客様がそれに依拠することを含むがこれに限定されない）について、当社がいかなる方法でも（お客様が被る可能性のある損失または傷害に関するものを含むがこれに限定されない）責任を負わないことを確認し、これに同意するものとします。当社は、第三者のコンテンツに関して、いかなる表明も保証も行いません。
          </TypographyV3>
          <TypographyV3>
            お客様は、(i)
            お客様のアカウントまたは認証情報を使用した人物を含むお客様の本サービスの利用、(ii)
            お客様またはお客様のアカウントまたは認証情報を使用した人物による本契約の違反、(iii)
            お客様のアカウントまたは認証情報に関連して使用、保存、または送信された情報、(iv)
            第三者の権利の侵害に起因して、あらゆる人物が主張する訴訟、原因、クレーム、損害、債務、要求、または責任（合理的な費用と弁護士費用を含む）について、Oura、その子会社、関連会社、役員、取締役、従業員、代表者、代理人、パートナー、ライセンサー、後継者、および譲受人を補償し、無害に保ち、防御することに同意するものとします。(ii)
            お客様またはお客様のアカウントや認証情報を使用する者による本契約の違反、(iii)
            お客様のアカウントや認証情報に関連して使用、保存、送信された情報、(iv)
            お客様またはお客様のアカウントや認証情報を使用する者によるプライバシ
            ー、パブリシティー、知的財産権、その他の所有権を含むがこれらに限定されない第三者の権利の侵害、または
            (v) 法律、規制、その他の法的要件の違反。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            カリフォルニア州のユーザーへのお知らせ
          </TypographyV3>
          <TypographyV3>
            カリフォルニア州民法第1789.3条に基づき、本サービスのカリフォルニア州のユーザーは、以下の特定の消費者権利の通知を受ける権利があります。
          </TypographyV3>
          <TypographyV3>
            カリフォルニア州消費者局の消費者サービス部門の苦情支援ユニットは、1625
            N. Market Blvd.、Suite N 112、Sacramento、California
            95834に書面で連絡するか、（800）952-5210に電話で連絡することができます。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            解約、キャンセル
          </TypographyV3>
          <TypographyV3>
            本契約は、本契約に基づいて解約またはキャンセルされるまで、有効に存続するものとします。
          </TypographyV3>
          <TypographyV3>
            Ouraは、(i)
            理由の如何を問わず、お客様に30日前に通知することにより、(ii)
            お客様が料金の不払い以外の本契約の重大な違反をした場合、または (iii)
            料金の不払いがあった場合に、本契約を終了する権利を有するものとします。上記にかかわらず、Ouraは、独自の裁量により、いつでも、いかなる理由でも、予告なしに、本サービスの全部または一部を削除、変更、一時停止、またはアクセス不能にする権利を留保します。
          </TypographyV3>
          <TypographyV3>
            お客様は、理由の如何を問わず、30日前にメール（support@ouraring.com）でOuraに通知することにより、本契約を終了することができます。お客様は、通知期間前および通知期間中に発生したすべての料金について責任を負うものとします。
          </TypographyV3>
          <TypographyV3>
            「注意事項」、「知的財産権」、「データおよび通信」、「補償」、「保証に関する免責事項」、「責任制限」、「準拠法」、「フォーラム」、「強制的拘束力のある仲裁」、「集団訴訟の権利放棄」と題されたセクション、および通知期間前および通知期間中に発生した料金の支払い義務は、理由の如何を問わず、本契約の終了後も存続するものとします。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            連邦政府の最終使用制限
          </TypographyV3>
          <TypographyV3>
            お客様が米国連邦政府の省庁である場合、または当該省庁を代表して契約する場合、本サービスは、48
            C.F.R. §2.101で定義される「商業品目」（Commercial Items）であり、48
            C.F.R. §12.212または48 C.F.R. §227.7202
            で使用される「商業コンピュータソフトウェア」（Commercial Computer
            Software）および「商業コンピュータソフトウェア文書」（Commercial
            Computer Software Documentation）で構成されます。48 C.F.R.
            §12.212または48 C.F.R. §227.7202-1から
            227.7202-4（該当する場合）に準拠して、本サービスは、本契約の条件に基づいて提供される権利のみをお客様に許諾します。お客様が米国連邦政府で発注業務に従事している場合、または当該発注業務の代理人としてGSAスケジュール契約を通じて購入する当該発注業務者の従業員である場合、公開された本契約およびプライバシーポリシーの条件は異なる場合があります。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            輸出コンプライアンスと使用制限
          </TypographyV3>
          <TypographyV3>
            お客様は、直接または間接に、本サービスまたはそれに関連する技術情報を、適用されるすべての米国輸出管理法および規制を含むがこれに限定されない適用法で禁止または制限される目的地または人に輸出または再輸出しないものとします。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            準拠法、フォーラム、強制的拘束力のある仲裁、集団訴訟の権利放棄
          </TypographyV3>
          <TypographyV3>
            本契約、本サービス、およびお客様とOuraの関係に関連するすべての行為は、抵触法の原則にかかわらず、カリフォルニア州法に準拠して解釈されるものとします。お客様は、本契約または本サービスに起因または関連するすべての紛争または請求を、単一の仲裁人による最終的かつ拘束力のある仲裁によって解決することに同意するものとします。これには、この「強制仲裁条項」の解釈または適用に起因する、または関連する紛争（その執行可能性、取消可能性、または有効性を含む）が含まれます。上記にかかわらず、いずれの当事者も、本サービスの不正使用もしくは乱用、または知的財産権の侵害を阻止するための差止命令による救済のみを目的として訴訟を提起することができます。強制仲裁条項を条件として、両当事者は、本契約に基づいて、または本契約に関連して生じる請求を解決または執行するための訴訟を、カリフォルニア州サンフランシスコの連邦裁判所または州裁判所に提起することに取消不能の形で同意し、各当事者は、当該訴訟、行為、または手続きにおいて、取消不能の形で当該裁判所の専属管轄権に服するものとします。適用される法律で禁止されている場合を除き、両当事者は、本サービスの利用または本契約に起因または関連するあらゆる請求または訴因は、当該請求または訴因が生じてから1年以内に提起されなければならず、または永久に禁止されることに同意します。本項は、欧州連合に居住するユーザーには適用されません。お客様が欧州連合に居住するユーザーである場合、本契約にはフィンランドの法律が適用され、本契約に関連して生じる紛争の審理はフィンランドの裁判所が独占的に行うものとします。本規定は、契約が消費者の国の現地法に準拠することを要求する国の消費者には適用されないものとします。本契約に関するすべての文書、通知、解釈は英語で行われるものとします。また、お客様は、法律で禁止されている場合や裁判所が公序良俗に反すると判断した場合を除き、集団訴訟または代表訴訟の代表者または構成員としてOuraに対して請求権を主張する権利を放棄することに同意するものとします。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            雑則
          </TypographyV3>
          <TypographyV3>
            お客様は、Ouraが本契約の遵守を確認するために本サービスの使用を監視する権利を有することを確認します。
          </TypographyV3>
          <TypographyV3>
            本契約のいずれかの条項、規定、または条件について、行為の有無にかかわらず、1つまたは複数の事例において権利を放棄しても、類似しているか否かにかかわらず、本契約の他の条項、規定、または条件について権利を放棄したものとはみなされず、また、そのような権利放棄が本契約のいずれかの条項、規定、または条件について継続的に権利を放棄したものとはみなされません。いかなる権利放棄も、権利放棄を行った当事者が書面で実行しない限り、拘束力を持たないものとします。
          </TypographyV3>
          <TypographyV3>
            お客様は、本契約を他の当事者に譲渡することはできず、そのような試みは無効となります。
          </TypographyV3>
          <TypographyV3>
            本契約のいずれかの条項が違法または執行不能であると判断された場合、当該条項は可能な限り最大限に執行され、その他の条項は完全に有効かつ執行可能なままとなります。
          </TypographyV3>
          <TypographyV3>
            本契約およびプライバシーポリシーは、本サービスに関するお客様とOuraとの間の完全かつ排他的な合意を構成するものであり、口頭か書面かを問わず、両当事者間の事前または同時のすべての通信、表明、声明、および了解に優先します。
          </TypographyV3>
          <TypographyV3>
            本契約の条項とプライバシーポリシーの条項との間に矛盾がある場合、本契約の条項が優先されるものとします。
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            規約・サービスの変更
          </TypographyV3>
          <TypographyV3>
            Ouraは、本契約および/またはプライバシーポリシーを、いつでも、いかなる理由でも、独自の裁量により、更新された条件を掲載することで更新する権利を留保します。Ouraによる別段の指示がある場合を除き、変更は掲載日から将来的に有効になります。本契約またはサービスに重大な変更があった場合、Ouraはお客様に通知します。当社が変更の通知を行った後、お客様が本サービスへのアクセスまたは使用を継続することにより、お客様は変更された本契約に拘束されることに同意したことになります。変更後の本契約をお客様が受け入れられない場合、お客様の唯一の手段は本サービスの利用を中止することです。Ouraおよび第三者サービスプロバイダーは、いつでも、いかなる理由でも、独自の裁量で、記載されたサービス、機能、および価格の改善および/または変更を行うことができます。モバイルアプリは、本サービスを改善、強化、および発展させるために、アップグレード、アップデート、および追加機能をダウンロードおよびインストールすることができます。当社は、本サービスまたはその一部を、予告の有無にかかわらず、一時的または永続的に変更または中止する権利を有します。お客様は、本サービスの変更、停止または中止について、お客様または第三者に対して、Ouraが責任を負わないことに同意するものとします。
          </TypographyV3>
          {enable2024Terms ? (
            <TermsOfDeliverySection />
          ) : (
            <>
              <TypographyV3 Element="h1" variant="h1" className="mt-20">
                ŌURA配達規約
              </TypographyV3>
              <TypographyV3>
                <em>最終更新日: 2021年10月18日</em>
              </TypographyV3>
              <TypographyV3 Element="h2" variant="h3">
                欧州連合と米国
              </TypographyV3>
              <TypographyV3>DDP - 関税込み持込渡し</TypographyV3>
              <TypographyV3>
                Ouraは、購入者の国の指定された場所に商品を届ける責任があり、輸入関税や税金を含む商品を目的地に届けるためのすべての費用を支払います。
              </TypographyV3>
              <TypographyV3 Element="h2" variant="h3">
                欧州連合および米国以外
              </TypographyV3>
              <TypographyV3>DAP - 仕向地持込渡し</TypographyV3>
              <TypographyV3>
                Ouraは、輸入通関に関連する費用を除き、指定された場所までの運送費を支払い、商品が買い手による荷降ろしの準備ができる時点までのすべてのリスクを負うものとします。
              </TypographyV3>
              <TypographyV3>以前の利用規約</TypographyV3>
            </>
          )}
          <TypographyV3 className="mt-40">
            {t('legal_disclaimer_translations')}
          </TypographyV3>
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayoutV3>
  );
};

Page.pageName = '利用規約';
Page.isSormusCompatible = true;

export default Page;
